"use client";

import { useEffect, useState } from "react";
import { blogPreviewApiPath } from "@/app/lib/blogPreviewUrl";
import { readBlogLivePreviewCache } from "@/app/lib/blogPreviewCache";
import { withAppBasePath } from "@/app/lib/withAppBasePath";

export type BlogPreviewIframeProps = {
  campaignId: string;
  blogId: string;
  supplierId?: string;
  title?: string;
  version?: number;
  className?: string;
  style?: React.CSSProperties;
};

function isHtmlResponse(text: string): boolean {
  const t = text.trim();
  return t.startsWith("<!") || t.startsWith("<html");
}

/** Loads blog HTML via BFF — server draft first, then session-cached layout POST. */
export default function BlogPreviewIframe({
  campaignId,
  blogId,
  supplierId,
  title,
  version,
  className,
  style,
}: BlogPreviewIframeProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError(null);
      setHtml(null);

      const cached = readBlogLivePreviewCache(blogId);
      const getUrl = blogPreviewApiPath({
        campaignId,
        blogId,
        supplierId,
        layout: "wireframe",
        version,
      });

      if (getUrl) {
        try {
          const res = await fetch(getUrl, { cache: "no-store", credentials: "same-origin" });
          const text = await res.text();
          if (!cancelled && res.ok && isHtmlResponse(text)) {
            setHtml(text);
            return;
          }
        } catch {
          /* fall through to POST with cached layout */
        }
      }

      const inline = cached ?? null;
      if (!inline?.cmsTemplateJson) {
        if (!cancelled) {
          setError(
            "Preview unavailable. Open this blog from the campaign wizard after Generate, or save the draft first.",
          );
        }
        return;
      }

      try {
        const res = await fetch(withAppBasePath("/api/blog-preview"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            campaignId,
            blogId,
            supplierId: supplierId ?? inline?.supplierId,
            title: title ?? inline?.title,
            layout: "wireframe",
            cmsTemplateJson: inline?.cmsTemplateJson,
          }),
        });
        const text = await res.text();
        if (cancelled) return;
        if (res.ok && isHtmlResponse(text)) {
          setHtml(text);
          return;
        }
        setError(text.slice(0, 400) || "Preview unavailable");
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Preview unavailable");
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [blogId, campaignId, supplierId, title, version]);

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center bg-white p-6">
        <p className="max-w-lg text-sm text-slate-600">{error}</p>
      </div>
    );
  }

  if (!html) {
    return (
      <div className="flex min-h-[200px] items-center justify-center bg-white p-6">
        <p className="text-sm text-slate-500">Loading preview…</p>
      </div>
    );
  }

  return (
    <iframe
      title={title?.trim() || "Blog preview"}
      srcDoc={html}
      className={className}
      style={style}
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
    />
  );
}
